/**
 * Environment Configuration
 * 
 * Access environment variables in a centralized way.
 * In Vite, environment variables must be prefixed with VITE_ to be accessible.
 * 
 * Usage:
 *   import { config } from '../shared/config/env'
 *   if (config.testMode) { ... }
 *   const domain = config.productDomain
 */

import { logger } from '../utils/logger'

/**
 * Log the import.meta.env object
 * @type {object}
 */
logger.debug('import.meta.env:', JSON.stringify(import.meta.env, null, 2));

export const config = {
  /**
   * Test mode flag
   * @type {boolean}
   */
  testMode: import.meta.env.VITE_TEST_MODE === 'true' || import.meta.env.VITE_TEST_MODE === true,
  
  /**
   * Product domain URL
   * @type {string}
   */
  productDomain: import.meta.env.VITE_PRODUCT_DOMAIN || 'https://sunlight.com',
  
  /**
   * Development mode
   * @type {boolean}
   */
  isDev: import.meta.env.DEV,
  
  /**
   * Production mode
   * @type {boolean}
   */
  isProd: import.meta.env.PROD,
  
  /**
   * Current mode
   * @type {string}
   */
  mode: import.meta.env.MODE,
  
  /**
   * Stripe publishable key
   * @type {string}
   */
  stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
  
  /**
   * Stripe backend URL
   * @type {string}
   */
  stripeBackendUrl: import.meta.env.VITE_STRIPE_BACKEND_URL || 'http://wpe.sunlight.com',
  
  /**
   * Google Analytics Measurement ID
   * @type {string}
   */
  googleAnalyticsId: import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-GEPL9QC9CR',
  
  /**
   * Google Analytics Domain (for cross-domain tracking) - Optional
   * @type {string}
   */
  googleAnalyticsDomain: import.meta.env.VITE_GA_DOMAIN || '',
  
  /**
   * Microsoft Clarity Project ID (only runs in production)
   * @type {string}
   */
  clarityProjectId: import.meta.env.VITE_CLARITY_PROJECT_ID || '',
  
  /**
   * Google Ads Conversion ID (only runs in production)
   * @type {string}
   */
  googleAdsConversionId: import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID || '',
  
  /**
   * Microsoft Ads UET Tag ID (only runs in production)
   * @type {string}
   */
  microsoftAdsTagId: import.meta.env.VITE_MICROSOFT_ADS_TAG_ID || '187246062',
  
  /**
   * Google Tag Manager Container ID
   * @type {string}
   */
  gtmContainerId: import.meta.env.VITE_GTM_CONTAINER_ID || 'GTM-T5XL9HWR',

  /**
   * Meta (Facebook) Pixel ID
   * Used for browser-side tracking alongside server-side Conversions API.
   * @type {string}
   */
  metaPixelId: import.meta.env.VITE_META_PIXEL_ID || '733719773086329',

  /**
   * Second Meta (Facebook) Pixel ID
   * Loaded alongside the primary pixel; all fbq('track') calls fire to both.
   * @type {string}
   */
  metaPixelId2: import.meta.env.VITE_META_PIXEL_ID_2 ?? '2278378966332864',

  /**
   * All configured Meta Pixel IDs (non-empty, de-duplicated)
   * @type {string[]}
   */
  get metaPixelIds() {
    return [...new Set([this.metaPixelId, this.metaPixelId2].filter(Boolean))]
  },

  /**
   * TikTok Pixel ID (only runs in production when VITE_TEST_MODE=false)
   * @type {string}
   */
  tiktokPixelId: import.meta.env.VITE_TIKTOK_PIXEL_ID || 'D5TN8HBC77UD675HSU10',

  /**
   * Whop Pixel business / scope ID
   * Used by whop.setScope(...). Find it at https://whop.com/dashboard/{biz_id}/pixel
   * @type {string}
   */
  whopBusinessId: import.meta.env.VITE_WHOP_BUSINESS_ID || 'biz_zKWoc4ppnxZjrz',
  
  /**
   * CareValidate API Key
   * @type {string}
   */
  carevalidateApiKey: import.meta.env.VITE_CAREVALIDATE_API_KEY || 'lBnEacqnU2n/E5VgxZbswyfV1QHDQab3NDnZykI2QYc=',
  
  /**
   * CareValidate API URL
   * @type {string}
   */
  carevalidateApiUrl: import.meta.env.VITE_CAREVALIDATE_API_URL || 'https://api.care360-next.carevalidate.com/api/v1',

  /**
   * CareValidate Stripe Publishable Key
   * @type {string}
   */
  carevalidateStripeKey: import.meta.env.VITE_CAREVALIDATE_STRIPE_PUBLISHABLE_KEY || 'pk_live_51U5qKM9wbuamr13bXMClU3z1foi1ZHeZF4fFOgmmeoCkOuzGdrIveKPYH3TSrys3aXd1YHQPviSHhTxD2OWLsVQV00CwH5qO1z',
  
  /**
   * CareValidate Form ID
   * @type {string}
   */
  carevalidateFormId: import.meta.env.VITE_CAREVALIDATE_FORM_ID || '0d9b7899-01ab-4dfe-a39c-dec9e35cd2b4',

  /**
   * CareValidate NAD Form ID
   * @type {string}
   */
  carevalidateNadFormId: import.meta.env.VITE_CAREVALIDATE_NAD_FORM_ID || '6a0ab1f2-072f-4313-b4a2-adc24277be8',
  
  /**
   * CareValidate Product Bundle ID
   * @type {string}
   */
  carevalidateProductBundleId: import.meta.env.VITE_CAREVALIDATE_PRODUCT_BUNDLE_ID || '021802a1-a4f2-4586-95aa-a32a2ee7a0e0',

  /**
   * CareValidate NAD Product Bundle ID
   * @type {string}
   */
  carevalidateNadProductBundleId: import.meta.env.VITE_CAREVALIDATE_NAD_PRODUCT_BUNDLE_ID || '74cefe7e-4bb4-4fd6-8022-92b735658a47',

  /**
   * CareValidate Sermorelin Form ID
   * @type {string}
   */
  carevalidateSermorelinFormId: import.meta.env.VITE_CAREVALIDATE_SERMORELIN_FORM_ID || 'f18fcc64-ba37-4ee1-bc6d-0838a7374c02',
  /**
   * CareValidate Sermorelin Product Bundle ID
   * @type {string}
   */
  carevalidateSermorelinProductBundleId: import.meta.env.VITE_CAREVALIDATE_SERMORELIN_PRODUCT_BUNDLE_ID || '636b25ed-8e5c-4e52-b031-55948523cdb2',
 
  /**
   * Sentry DSN (Data Source Name)
   * @type {string}
   */
  sentryDsn: import.meta.env.VITE_SENTRY_DSN || 'https://dac6af050f6973cc34a2bc0e233f798b@o4505606054871040.ingest.us.sentry.io/4510821805719552',
  
  /**
   * Enable/Disable Sentry error tracking
   * @type {boolean}
   */
  sentryEnabled: import.meta.env.VITE_SENTRY_ENABLED === 'true',
  
  /**
   * Sentry environment name
   * @type {string}
   */
  sentryEnvironment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 
    (import.meta.env.PROD ? 'production' : 
     import.meta.env.MODE === 'staging' ? 'staging' : 'development'),
  
  /**
   * Sentry log level (error, warning, info, debug)
   * @type {string}
   */
  sentryLogLevel: import.meta.env.VITE_SENTRY_LOG_LEVEL || 
    (import.meta.env.PROD ? 'warning' : 
     import.meta.env.MODE === 'staging' ? 'error' : 'debug'),
  
  /**
   * Sentry traces sample rate (0.0 to 1.0)
   * @type {number}
   */
  sentryTracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
  
  /**
   * Sentry release version
   * @type {string}
   */
  sentryRelease: import.meta.env.VITE_SENTRY_RELEASE || 'sunlight-funnel@1.0.0',

  /**
   * A/B experiment URL query param name (e.g. ?_vt=checkout_alt).
   * Must match the server-side test urlParam when using checkout_experiment.
   * @type {string}
   */
  abExperimentUrlParam: import.meta.env.VITE_AB_EXPERIMENT_URL_PARAM || '_vt',

  /**
   * A/B experiment cookie name (e.g. ab_checkout_experiment).
   * Must match the server-side test cookieName when using checkout_experiment.
   * @type {string}
   */
  abExperimentCookieName: import.meta.env.VITE_AB_EXPERIMENT_COOKIE_NAME || 'ab_checkout_experiment_v4',

  /**
   * Default checkout experiment variant when URL/cookie has no assignment.
   * Used as fallback in tracking and checkout components (e.g. checkout_v8).
   * @type {string}
   */
  defaultCheckoutExperimentVariant: import.meta.env.VITE_DEFAULT_CHECKOUT_EXPERIMENT_VARIANT || 'checkout_v8',
  
  /**
   * Enable/disable backend data loading by entry_id
   * 
   * When enabled: Users can access /cv-1/checkout?entry_id=xxx and data loads from backend
   * When disabled: Users must complete funnel from start (data only from localStorage)
   * 
   * Set to false if backend is not ready
   * 
   * Related docs: FUNNEL_DATA_LOADING_BY_ENTRY_ID.md, CENTRALIZED_DATA_LOADING_REFACTOR.md
   * 
   * @type {boolean}
   */
  enableBackendDataLoading: false,
  
  /**
   * DQ (Disqualification) blocking period in days
   * 
   * When a user is disqualified, they cannot retake the quiz for this duration.
   * After this period expires, they can restart the quiz.
   * 
   * Default: 7 days (production)
   * For testing: Set to fraction of a day (e.g., 0.0007 = ~1 minute)
   * 
   * Examples:
   * - 7 = 7 days (production)
   * - 1 = 1 day
   * - 0.0417 = 1 hour (60 minutes)
   * - 0.0069 = 10 minutes
   * - 0.0007 = 1 minute
   * 
   * @type {number}
   */
  dqBlockingPeriodDays: parseFloat(import.meta.env.VITE_DQ_BLOCKING_PERIOD_DAYS || '7'), // 7 days
  
  /**
   * DQ blocking cookie name
   * 
   * Name of the cookie used to store DQ timestamp.
   * Specific to variant-cv-1 funnel.
   * 
   * @type {string}
   */
  dqBlockingCookieName: import.meta.env.VITE_DQ_BLOCKING_COOKIE_NAME || 'dq_timestamp_cv1',
  
  /**
   * Cookie domain for cross-subdomain support
   * 
   * Leave empty to auto-detect:
   * - localhost: no domain (works on localhost)
   * - *.sunlight.com: .sunlight.com (works on all subdomains)
   * 
   * @type {string}
   */
  cookieDomain: import.meta.env.VITE_COOKIE_DOMAIN || '',
}

export default config

